/**
 * Money formatting + parsing helpers for Fias Leads deal values.
 *
 * Deal values are entered as free text so users can paste "$250,000" or
 * type "250,000" without the browser's native number input silently
 * dropping the commas — the old `type="number"` field rejected anything
 * non-numeric with no warning. We sanitize to a parseable numeric string,
 * parse it, and re-display grouped for readability.
 */

/** Keep only digits and a single decimal point. */
export function sanitizeMoneyText(raw: string): string {
  let cleaned = raw.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot !== -1) {
    // Collapse any further dots after the first.
    cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
  }
  return cleaned;
}

/** Parse user-entered money text (commas, currency symbols, spaces ok). */
export function parseMoneyInput(raw: string): number {
  const n = Number(sanitizeMoneyText(raw));
  return Number.isFinite(n) ? n : 0;
}

/** Group a numeric value with thousands separators, up to 2 decimals. */
export function formatMoneyValue(value: number | string): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/** "USD 250,000" style label used across cards / lists / detail. */
export function formatCurrency(value: number | string, currency: string): string {
  return `${currency} ${formatMoneyValue(value)}`;
}

/**
 * Compact relative time ("3d ago", "just now") for last-activity display.
 * Returns "—" for a nullish / unparseable timestamp (i.e. never contacted).
 */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const sec = Math.round((Date.now() - then) / 1000);
  if (sec < 0) return 'just now';
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}
